import { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { toast } from 'sonner'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { dealMatchesSearch, stageOrder } from '@/components/Deals/deals-constants'
import type { Deal, DealStage } from '@/types'

export type RocketTab = 'pipeline' | 'history' | 'trash'

const STAGE_LEGACY_MAP: Record<string, string> = {
  reuniaoagendada: 'reuniao_agendada',
  reuniaorealizada: 'reuniao_realizada',
  propostaelaboracao: 'proposta_elaboracao',
  propostaenviada: 'proposta_enviada',
  emrenegociacao: 'renegociacao',
  renegociacao: 'renegociacao',
  gerarcontrato: 'gerar_contrato',
  contratoenviado: 'contrato_enviado',
  fechamentofuturo: 'fechamento_futuro',
}

function normalizeRocketStage(etapa: string): DealStage {
  const cleaned = String(etapa || '').toLowerCase().replace(/-/g, '_')
  return (STAGE_LEGACY_MAP[cleaned] || etapa) as DealStage
}

export function useRocketSearch(isAdmin: boolean) {
  const [rocketDeals, setRocketDeals] = useState<Deal[]>([])
  const [deletedDeals, setDeletedDeals] = useState<Deal[]>([])
  const [loadingDeals, setLoadingDeals] = useState(false)
  const [activeTab, setActiveTab] = useState<RocketTab>('pipeline')
  const [closerFilter, setCloserFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [etapaFilter, setEtapaFilter] = useState('all')
  const [closers, setClosers] = useState<{ id: string; name: string }[]>([])
  const [viewModeDeals, setViewModeDeals] = useState<'kanban' | 'list'>('kanban')
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [metricsVisible, setMetricsVisible] = useState(() => {
    const saved = localStorage.getItem('rocket_metrics_visible')
    return saved !== null ? JSON.parse(saved) : true
  })

  useEffect(() => {
    localStorage.setItem('rocket_metrics_visible', JSON.stringify(metricsVisible))
  }, [metricsVisible])

  const closedDeals = useMemo(
    () => rocketDeals.filter((d) => d.etapa === 'ganho' || d.etapa === 'perdido'),
    [rocketDeals],
  )

  const pipelineStages = useMemo(() => {
    const active = stageOrder.filter((s) => s !== 'ganho' && s !== 'perdido')
    const present = new Set(rocketDeals.map((d) => d.etapa))
    return active.filter((s) => present.has(s))
  }, [rocketDeals])

  const pipelineDeals = useMemo(
    () =>
      rocketDeals.filter((deal) => {
        if (!dealMatchesSearch(deal, searchTerm)) return false
        if (etapaFilter !== 'all' && deal.etapa !== etapaFilter) return false
        return true
      }),
    [rocketDeals, searchTerm, etapaFilter],
  )

  const loadRocketDeals = useCallback(async () => {
    setLoadingDeals(true)
    try {
      const params = new URLSearchParams()
      if (closerFilter) params.append('closerId', closerFilter)
      const queryString = params.toString()
      const url = `/rocket-deals${queryString ? `?${queryString}` : ''}`

      const { data } = await api.get(url)
      let rocketDealsData: Deal[] = data.data ?? data ?? []

      rocketDealsData = rocketDealsData.map((d) => ({
        ...d,
        etapa: normalizeRocketStage(d.etapa),
      }))
      setRocketDeals(rocketDealsData.filter((d) => !d.etapa?.startsWith('deleted_')))
      setDeletedDeals(rocketDealsData.filter((d) => d.etapa?.startsWith('deleted_')))
    } catch (err: unknown) {
      setRocketDeals([])
      setDeletedDeals([])
      toast.error(getFriendlyError(err, 'Erro ao carregar pipeline Rocket'))
    } finally {
      setLoadingDeals(false)
    }
  }, [closerFilter])

  useEffect(() => {
    loadRocketDeals()
  }, [loadRocketDeals])

  useEffect(() => {
    if (!isAdmin) return
    async function loadClosers() {
      try {
        const { data } = await api.get('/users?role=closer')
        setClosers(data.data ?? data ?? [])
      } catch (err: unknown) {
        setClosers([])
        toast.error(getFriendlyError(err, 'Erro ao carregar closers'))
      }
    }
    loadClosers()
  }, [isAdmin])

  const handleMoveStage = useCallback(
    async (dealId: string, newStage: DealStage) => {
      try {
        const deal = rocketDeals.find((d) => d.id === dealId)
        const payload: Record<string, unknown> = { etapa: newStage }

        if (newStage === 'proposta_elaboracao' || newStage === 'proposta_enviada') {
          payload.budgetConfirmado =
            deal?.budgetConfirmado && String(deal.budgetConfirmado).trim().length > 0
              ? deal.budgetConfirmado
              : 'A confirmar'
          payload.decisorPresente =
            deal?.decisorPresente && String(deal.decisorPresente).trim().length > 0
              ? deal.decisorPresente
              : 'A confirmar'
          payload.doresIdentificadas =
            deal?.doresIdentificadas && String(deal.doresIdentificadas).trim().length > 0
              ? deal.doresIdentificadas
              : 'Em diagnóstico'
        }

        await api.patch(`/rocket-deals/${dealId}`, payload)
        toast.success('Deal movido com sucesso!')
        loadRocketDeals()
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao mover deal'))
      }
    },
    [loadRocketDeals, rocketDeals],
  )

  const handleSoftDelete = useCallback(
    async (dealId: string) => {
      try {
        await api.delete(`/rocket-deals/${dealId}`)
        toast.success('Deal movido para lixeira')
        loadRocketDeals()
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.response) {
          toast.error(getFriendlyError(err, 'Erro ao excluir deal'))
          return
        }
        setRocketDeals((prev) => prev.filter((d) => d.id !== dealId))
        toast.success('Deal removido da pipeline')
      }
    },
    [loadRocketDeals],
  )

  const handleRestore = useCallback(
    async (dealId: string) => {
      if (!dealId) return
      try {
        await api.post(`/rocket-deals/${dealId}/restore`)
        toast.success('Deal restaurado para Pipeline')
        loadRocketDeals()
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao restaurar deal'))
      }
    },
    [loadRocketDeals],
  )

  return {
    rocketDeals,
    deletedDeals,
    closedDeals,
    pipelineDeals,
    pipelineStages,
    loadingDeals,
    activeTab,
    setActiveTab,
    closerFilter,
    setCloserFilter,
    searchTerm,
    setSearchTerm,
    etapaFilter,
    setEtapaFilter,
    closers,
    viewModeDeals,
    setViewModeDeals,
    selectedDeal,
    setSelectedDeal,
    isCreateModalOpen,
    setIsCreateModalOpen,
    metricsVisible,
    setMetricsVisible,
    loadRocketDeals,
    handleMoveStage,
    handleSoftDelete,
    handleRestore,
  }
}
